import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Empty,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useState } from 'react';
import { catalogueApi } from '@/features/catalogue/catalogue.api';
import { GuardrailsCard } from '@/features/guardrails/ui/GuardrailsCard';
import { LiveMetaAdsCard } from './LiveMetaAdsCard';
import { useActiveOrg } from '@/features/organizations/active-org-context';
import { formatMoney } from '@/lib/format';
import {
  campaignsApi,
  type Campaign,
  type CampaignObjective,
  type CampaignStatus,
} from '../campaigns.api';

const STATUS_COLORS: Record<CampaignStatus, string> = {
  draft: 'default',
  pending_approval: 'gold',
  active: 'green',
  paused: 'orange',
  rejected: 'red',
  completed: 'blue',
};

/** Live performance line for an active/paused campaign. */
function PerformanceLine({
  orgId,
  projectId,
  campaignId,
}: {
  orgId: string;
  projectId: string;
  campaignId: string;
}) {
  const { activeOrg } = useActiveOrg();
  const currency = activeOrg?.settings.currency ?? 'USD';
  const { data } = useQuery({
    queryKey: ['campaign-perf', orgId, projectId, campaignId],
    queryFn: () => campaignsApi.performance(orgId, projectId, campaignId),
  });
  if (!data) return null;
  const roasColor =
    data.roas === 0 ? 'default' : data.roas < 1 ? 'red' : data.roas >= 3 ? 'green' : 'blue';
  return (
    <Space wrap size={4} style={{ marginBottom: 8 }}>
      <Tag color={roasColor}>ROAS {data.roas}</Tag>
      <Tag>Spend {formatMoney(data.spend, currency)}</Tag>
      <Tag>Sales {formatMoney(data.sales, currency)}</Tag>
      <Tag>CTR {(data.ctr * 100).toFixed(2)}%</Tag>
      <Tag>Reach {data.reach}</Tag>
    </Space>
  );
}

const OBJECTIVES: CampaignObjective[] = [
  'awareness',
  'traffic',
  'engagement',
  'conversions',
  'sales',
];

export function CampaignsTab({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const { activeOrg } = useActiveOrg();
  const currency = activeOrg?.settings.currency ?? 'USD';
  const queryClient = useQueryClient();
  const key = ['campaigns', orgId, projectId] as const;

  const query = useQuery({ queryKey: key, queryFn: () => campaignsApi.list(orgId, projectId) });
  const productsQuery = useQuery({
    queryKey: ['products-all', orgId, projectId],
    queryFn: () => catalogueApi.list(orgId, projectId, 1, 100),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const [name, setName] = useState('');
  const [objective, setObjective] = useState<CampaignObjective>('sales');
  const [productIds, setProductIds] = useState<string[]>([]);
  const [budget, setBudget] = useState<number>(100);

  const create = useMutation({
    mutationFn: () =>
      campaignsApi.create(orgId, projectId, {
        name,
        objective,
        productIds,
        requestedBudget: budget,
      }),
    onSuccess: () => {
      void invalidate();
      setName('');
      setProductIds([]);
      message.success('Campaign created');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to create campaign'),
  });

  const plan = useMutation({
    mutationFn: (id: string) => campaignsApi.plan(orgId, projectId, id),
    onSuccess: () => {
      message.success('Planning queued — approve the launch from Agents → Approvals');
      setTimeout(() => void invalidate(), 3000);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to plan'),
  });

  const statusMut = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'pause' | 'resume' | 'delete' }) => {
      if (action === 'pause') await campaignsApi.pause(orgId, projectId, id);
      else if (action === 'resume') await campaignsApi.resume(orgId, projectId, id);
      else await campaignsApi.remove(orgId, projectId, id);
    },
    onSuccess: () => {
      void invalidate();
      message.success('Updated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed'),
  });

  const monitor = useMutation({
    mutationFn: () => campaignsApi.monitor(orgId, projectId),
    onSuccess: () => message.success('Monitoring queued'),
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to monitor'),
  });

  const optimize = useMutation({
    mutationFn: (id: string) => campaignsApi.optimize(orgId, projectId, id),
    onSuccess: () => {
      message.success('Optimization queued — approve any proposed change from Agents → Approvals');
      setTimeout(() => void invalidate(), 3000);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to optimize'),
  });

  const productOptions = (productsQuery.data?.items ?? []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const campaigns = query.data ?? [];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <GuardrailsCard orgId={orgId} projectId={projectId} canEdit={canEdit} />
      <LiveMetaAdsCard orgId={orgId} projectId={projectId} />
      {canEdit ? (
        <Card size="small" title="Launch a campaign">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Input
                placeholder="Campaign name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: 220 }}
              />
              <Select<CampaignObjective>
                value={objective}
                onChange={setObjective}
                style={{ width: 150 }}
                options={OBJECTIVES.map((o) => ({ value: o, label: o }))}
              />
              <InputNumber
                min={0}
                prefix={currency}
                value={budget}
                onChange={(v) => setBudget(typeof v === 'number' ? v : 0)}
                placeholder="Budget"
              />
            </Space>
            <Select
              mode="multiple"
              placeholder="Select products to advertise"
              value={productIds}
              onChange={setProductIds}
              options={productOptions}
              style={{ width: '100%', maxWidth: 560 }}
            />
            <Button
              type="primary"
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={name.trim().length < 2 || productIds.length === 0}
            >
              Create campaign
            </Button>
          </Space>
        </Card>
      ) : null}

      {canEdit ? (
        <Space>
          <Button onClick={() => monitor.mutate()} loading={monitor.isPending}>
            Monitor active campaigns
          </Button>
          <Button onClick={() => invalidate()}>Refresh</Button>
        </Space>
      ) : null}

      {query.isLoading ? (
        <Spin />
      ) : campaigns.length === 0 ? (
        <Empty description="No campaigns yet" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {campaigns.map((c: Campaign) => (
            <Card
              key={c.id}
              size="small"
              title={
                <Space wrap>
                  {c.name}
                  <Tag color={STATUS_COLORS[c.status]}>{c.status.replace('_', ' ')}</Tag>
                  <Tag>{c.objective}</Tag>
                  <Tag
                    color={
                      c.meta.syncState === 'synced'
                        ? 'blue'
                        : c.meta.syncState === 'error'
                          ? 'red'
                          : 'default'
                    }
                  >
                    Meta: {c.meta.syncState.replace('_', ' ')}
                  </Tag>
                </Space>
              }
              extra={
                canEdit ? (
                  <Space>
                    {c.status === 'draft' ? (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => plan.mutate(c.id)}
                        loading={plan.isPending}
                      >
                        Plan &amp; request launch
                      </Button>
                    ) : null}
                    {c.status === 'active' ? (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => optimize.mutate(c.id)}
                        loading={optimize.isPending}
                      >
                        Optimize
                      </Button>
                    ) : null}
                    {c.status === 'active' ? (
                      <Button
                        size="small"
                        onClick={() => statusMut.mutate({ id: c.id, action: 'pause' })}
                      >
                        Pause
                      </Button>
                    ) : null}
                    {c.status === 'paused' ? (
                      <Button
                        size="small"
                        onClick={() => statusMut.mutate({ id: c.id, action: 'resume' })}
                      >
                        Resume
                      </Button>
                    ) : null}
                    <Popconfirm
                      title="Delete campaign?"
                      onConfirm={() => statusMut.mutate({ id: c.id, action: 'delete' })}
                    >
                      <Button size="small" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ) : null
              }
            >
              {c.status === 'active' || c.status === 'paused' ? (
                <PerformanceLine orgId={orgId} projectId={projectId} campaignId={c.id} />
              ) : null}
              {c.pendingOptimization ? (
                <Typography.Paragraph>
                  <Tag color="gold">optimization pending</Tag>
                  {c.pendingOptimization.kind}: {c.pendingOptimization.rationale}
                </Typography.Paragraph>
              ) : null}
              {c.plan.plannedAt ? (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Typography.Text>
                    <strong>Budget:</strong> {c.plan.budgetCurrency} {c.plan.budgetAmount}/
                    {c.plan.cadence}{' '}
                    <Tag
                      color={c.plan.source === 'llm' ? 'gold' : 'default'}
                      style={{ marginLeft: 8 }}
                    >
                      {c.plan.source}
                    </Tag>
                  </Typography.Text>
                  <Typography.Text>
                    <strong>Audience:</strong> {c.plan.audience}
                  </Typography.Text>
                  <Typography.Text>
                    <strong>Ad copy:</strong> {c.plan.adCopy}
                  </Typography.Text>
                  {c.plan.headlines.length > 0 ? (
                    <Typography.Text type="secondary">
                      Headlines: {c.plan.headlines.join(' · ')}
                    </Typography.Text>
                  ) : null}
                  {c.plan.creativeIdeas.length > 0 ? (
                    <Typography.Text type="secondary">
                      Creatives: {c.plan.creativeIdeas.join(' · ')}
                    </Typography.Text>
                  ) : null}
                  {c.plan.notes ? (
                    <Typography.Text italic type="secondary">
                      {c.plan.notes}
                    </Typography.Text>
                  ) : null}
                </Space>
              ) : (
                <Typography.Text type="secondary">
                  Not planned yet — the media buyer will draft budget, audience, and creatives.
                </Typography.Text>
              )}
              {c.optimizations.length > 0 ? (
                <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                  <Typography.Text strong>Optimization history</Typography.Text>
                  {c.optimizations
                    .slice()
                    .reverse()
                    .map((o, i) => (
                      <Typography.Paragraph key={i} type="secondary" style={{ margin: '4px 0 0' }}>
                        <Tag>{o.kind}</Tag>
                        {o.rationale}
                      </Typography.Paragraph>
                    ))}
                </div>
              ) : null}
            </Card>
          ))}
        </Space>
      )}
    </Space>
  );
}
